/**
 * OrgContacts - Corporate Contact List (CRM) for the Org Dashboard
 * Manages the Contacts tab in org-dashboard.html
 */

class OrgContacts {
  static contacts = [];
  static filteredContacts = [];
  static allTags = [];
  static activeTagFilter = '';
  static searchQuery = '';
  static initialized = false;
  static selectedContacts = new Set();

  // ============================================================================
  // INIT
  // ============================================================================

  static async init() {
    if (this.initialized) {
      await this.loadContacts();
      return;
    }
    this.initialized = true;
    this._setupSearch();
    this._setupTagFilter();
    await this.loadContacts();
  }

  static _setupSearch() {
    const searchInput = document.getElementById('contactsSearch');
    if (!searchInput) return;
    searchInput.addEventListener('input', () => {
      this.searchQuery = searchInput.value.trim().toLowerCase();
      this._applyFilters();
    });
  }

  static _setupTagFilter() {
    document.getElementById('contactsTagFilter')?.addEventListener('change', (e) => {
      this.activeTagFilter = e.target.value;
      this._applyFilters();
    });
  }

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  static async loadContacts() {
    this._showLoading(true);
    try {
      const data = await window.OrgContext.orgFetch('/contacts');
      this.contacts = (data.contacts || []).sort((a, b) => {
        const an = (a.name || a.email || a.phone || '').toLowerCase();
        const bn = (b.name || b.email || b.phone || '').toLowerCase();
        return an.localeCompare(bn);
      });
      this._extractTags();
      this._applyFilters();
      this._updateStats();
    } catch (err) {
      console.error('Failed to load contacts:', err);
      this._showError('Failed to load contacts. Please try again.');
    } finally {
      this._showLoading(false);
    }
  }

  static _extractTags() {
    const tagSet = new Set();
    this.contacts.forEach(c => (c.tags || []).forEach(t => tagSet.add(t)));
    // A to Z regardless of case ("VIP" used to come before "corporate").
    this.allTags = [...tagSet].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    this._renderTagFilterOptions();
  }

  static _renderTagFilterOptions() {
    const sel = document.getElementById('contactsTagFilter');
    if (!sel) return;
    const current = sel.value;
    sel.innerHTML = '<option value="">All Tags</option>' +
      this.allTags.map(t => `<option value="${this._esc(t)}" ${t === current ? 'selected' : ''}>${this._esc(t)}</option>`).join('');
  }

  static _applyFilters() {
    let result = [...this.contacts];

    if (this.searchQuery) {
      // The full name (first + last: "Dana Levi" found nothing, as the two are
      // stored apart) and what the table shows (#1095: company, address).
      const q = this.searchQuery;
      result = result.filter(c =>
        [c.name, c.lastName].filter(Boolean).join(' ').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q) ||
        (c.company || '').toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q) ||
        (c.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }

    if (this.activeTagFilter) {
      result = result.filter(c => (c.tags || []).includes(this.activeTagFilter));
    }

    this.filteredContacts = result;
    this._renderTable();
  }

  static _updateStats() {
    const totalEl = document.getElementById('contactsTotalCount');
    const tagsEl = document.getElementById('contactsTagsCount');
    if (totalEl) totalEl.textContent = this.contacts.length;
    if (tagsEl) tagsEl.textContent = this.allTags.length;
  }

  // ============================================================================
  // TABLE RENDERING
  // ============================================================================

  static _renderTable() {
    const tbody = document.getElementById('contactsTableBody');
    const emptyState = document.getElementById('contactsEmpty');
    if (!tbody) return;

    if (this.filteredContacts.length === 0) {
      tbody.innerHTML = '';
      if (emptyState) emptyState.style.display = 'block';
      this._updateSelectionUI();
      return;
    }

    if (emptyState) emptyState.style.display = 'none';

    // Address and notes are on the row (task #1095): the address in its own
    // column, cut short with an ellipsis (the whole of it on hover); notes as
    // a button the size of Edit, just before it (round 2: a small icon beside
    // the name went unnoticed), whose tooltip reads them and whose click opens
    // the contact, so a phone can read them too. Rows without notes keep an
    // empty slot of the same width so the Edit buttons stay in one column.
    // The row itself does nothing on a click (round 3): only the checkbox, the
    // buttons and the name, a link to the contact's card (its details and event
    // history, reachable no other way for a contact without notes). A native tooltip:
    // Bootstrap's throws when the table re-renders (every search keystroke)
    // while one is fading out.
    tbody.innerHTML = this.filteredContacts.map(c => {
      const note = String(c.notes || '').trim();
      const noteTip = note.length > 300 ? note.slice(0, 299) + '…' : note;
      return `
      <tr class="contacts-row">
        <td class="text-center">
          <input type="checkbox" class="form-check-input contact-select-cb"
                 data-id="${c.id}" ${this.selectedContacts.has(c.id) ? 'checked' : ''}
                 onchange="OrgContacts._toggleContactSelection('${c.id}', this.checked)">
        </td>
        <td>
          <button type="button" class="contact-name-link fw-semibold" onclick="OrgContacts.showDetailModal('${c.id}')">${c.name ? this._esc(c.name) + (c.lastName ? ' ' + this._esc(c.lastName) : '') : `<span class="text-muted fst-italic">${this._esc(c.email || c.phone || 'Unnamed contact')}</span>`}</button>
          ${(c.jobTitle || c.company) ? `<div class="small text-muted">${[c.jobTitle, c.company].filter(Boolean).map(v => this._esc(v)).join(' · ')}</div>` : ''}
        </td>
        <td class="text-muted small">${c.email ? this._esc(c.email) : '<span class="text-muted">—</span>'}</td>
        <td class="text-muted small">${c.phone ? this._esc(c.phone) : '<span class="text-muted">—</span>'}</td>
        <td class="text-muted small">${c.address ? `<div class="contact-cell-clip" dir="auto" title="${this._esc(c.address)}">${this._esc(c.address)}</div>` : '<span class="text-muted">—</span>'}</td>
        <td><div class="contact-tags">${this._tagPills(c.tags)}</div></td>
        <td class="text-end text-nowrap">
          ${note ? `<button type="button" class="btn btn-org-outline btn-sm contact-note-btn me-1" onclick="OrgContacts.showDetailModal('${c.id}')" aria-label="Notes: ${this._esc(noteTip)}" title="${this._esc(noteTip)}"><i class="bi bi-sticky-fill"></i></button>` : '<span class="btn btn-org-outline btn-sm contact-note-btn contact-note-slot me-1" aria-hidden="true"><i class="bi bi-sticky-fill"></i></span>'}
          <button class="btn btn-org-outline btn-sm me-1" onclick="OrgContacts.showEditModal('${c.id}')" aria-label="Edit contact">
            <i class="bi bi-pencil"></i>
          </button>
          <button class="btn btn-sm btn-outline-danger" onclick="OrgContacts.confirmDelete('${c.id}')" aria-label="Delete contact">
            <i class="bi bi-trash"></i>
          </button>
        </td>
      </tr>`;
    }).join('');

    this._updateSelectionUI();
  }

  /** A contact's tags as pills (task #1095) - the table, the contact window, the tag fields. */
  static _tagPills(tags) {
    return (tags || []).map(t => `<span class="contact-tag" dir="auto">${this._esc(t)}</span>`).join('');
  }

  // ============================================================================
  // TAG FIELD (task #1095)
  // A box of the contact's tags as pills, with a list of the tags already used
  // in this workspace that opens when it is clicked and narrows as you type;
  // a new tag is added with Enter or a comma. The tags are kept as a list, not
  // "a, b" text, so a tag with a comma in it (the RSVP sync's "Event: <event
  // title>") is no longer split in two by an edit.
  // ============================================================================

  static _tagFieldHtml(id, placeholder) {
    return `
      <div class="contact-tag-field form-control" id="${id}" data-placeholder="${this._esc(placeholder)}">
        <input type="text" class="contact-tag-entry" id="${id}Entry" autocomplete="off" maxlength="50" dir="auto"
               role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="${id}Menu">
      </div>`;
  }

  static _wireTagField(id, tags) {
    const field = document.getElementById(id);
    const entry = document.getElementById(id + 'Entry');
    if (!field || !entry) return;
    const norm = (t) => String(t || '').replace(/\s+/g, ' ').trim();
    const same = (a, b) => norm(a).toLowerCase() === norm(b).toLowerCase();
    field._tags = [];
    let menu = null;
    let active = -1;
    let options = [];

    const paintPills = () => {
      field.querySelectorAll('.contact-tag').forEach(p => p.remove());
      field._tags.forEach((t, i) => {
        const pill = document.createElement('span');
        pill.className = 'contact-tag';
        pill.dir = 'auto';
        pill.innerHTML = `<span>${this._esc(t)}</span><button type="button" class="contact-tag-x" tabindex="-1" aria-label="Remove ${this._esc(t)}">&times;</button>`;
        pill.querySelector('button').addEventListener('mousedown', (e) => { e.preventDefault(); field._tags.splice(i, 1); paintPills(); renderMenu(); });
        field.insertBefore(pill, entry);
      });
      entry.placeholder = field._tags.length ? '' : (field.dataset.placeholder || '');
    };
    const add = (raw) => {
      raw.split(',').map(norm).filter(Boolean).forEach(t => {
        if (!field._tags.some(x => same(x, t))) field._tags.push(t.slice(0, 50));
      });
      paintPills();
    };
    field._commitTyped = () => { if (entry.value.trim()) { add(entry.value); entry.value = ''; } };

    const closeMenu = () => {
      menu?.remove();
      menu = null;
      active = -1;
      entry.setAttribute('aria-expanded', 'false');
      entry.removeAttribute('aria-activedescendant');
      window.removeEventListener('resize', place);
      field.closest('.modal-body')?.removeEventListener('scroll', place);
    };
    // Fixed, inside the .modal (not its body): a scrolling window body would
    // otherwise cut the list off, and Bootstrap keeps focus inside the modal.
    function place() {
      if (!menu) return;
      const r = field.getBoundingClientRect();
      menu.style.left = r.left + 'px';
      menu.style.width = r.width + 'px';
      const h = menu.offsetHeight;
      const below = r.bottom + 4 + h <= window.innerHeight || r.top < h + 8;
      menu.style.top = (below ? r.bottom + 4 : r.top - 4 - h) + 'px';
    }
    const renderMenu = () => {
      if (!menu) return;
      const q = norm(entry.value);
      const used = this.allTags.filter(t => !field._tags.some(x => same(x, t)));
      options = used.filter(t => !q || t.toLowerCase().includes(q.toLowerCase())).map(t => ({ tag: t }));
      if (q && !this.allTags.some(t => same(t, q)) && !field._tags.some(x => same(x, q))) options.push({ tag: q, isNew: true });
      if (active >= options.length) active = options.length - 1;
      if (q && active < 0 && options.length) active = 0;
      menu.innerHTML = options.length
        ? options.map((o, i) => `<div class="contact-tag-option${i === active ? ' is-active' : ''}" role="option" id="${id}Opt${i}" data-i="${i}" aria-selected="${i === active}">${o.isNew ? `<i class="bi bi-plus-lg" aria-hidden="true"></i><span>Add "<bdi>${this._esc(o.tag)}</bdi>"</span>` : `<span class="contact-tag" dir="auto">${this._esc(o.tag)}</span>`}</div>`).join('')
        : `<div class="contact-tag-empty">${this.allTags.length ? 'Every tag is already added. Type to add a new one.' : 'No tags yet. Type a tag and press Enter.'}</div>`;
      if (active >= 0) entry.setAttribute('aria-activedescendant', `${id}Opt${active}`);
      else entry.removeAttribute('aria-activedescendant');
      place();
    };
    const openMenu = () => {
      if (menu) return renderMenu();
      menu = document.createElement('div');
      menu.className = 'contact-tag-menu';
      menu.id = id + 'Menu';
      menu.setAttribute('role', 'listbox');
      menu.addEventListener('mousedown', (e) => {
        e.preventDefault(); // keep the focus (and the list) in the field
        const opt = e.target.closest('.contact-tag-option');
        if (!opt) return;
        add(options[+opt.dataset.i].tag);
        entry.value = '';
        renderMenu();
      });
      (field.closest('.modal') || document.body).appendChild(menu);
      entry.setAttribute('aria-expanded', 'true');
      window.addEventListener('resize', place);
      field.closest('.modal-body')?.addEventListener('scroll', place);
      renderMenu();
    };

    field.addEventListener('mousedown', (e) => {
      if (e.target === entry || e.target.closest('.contact-tag-x')) return;
      e.preventDefault();
      entry.focus();
      openMenu();
    });
    entry.addEventListener('focus', openMenu);
    entry.addEventListener('click', openMenu);
    entry.addEventListener('blur', () => { field._commitTyped(); closeMenu(); });
    entry.addEventListener('input', () => {
      if (entry.value.includes(',')) {
        const parts = entry.value.split(',');
        entry.value = parts.pop();
        add(parts.join(','));
      }
      active = -1;
      openMenu();
    });
    entry.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!menu) openMenu();
        if (!options.length) return;
        active = e.key === 'ArrowDown' ? (active + 1) % options.length : (active <= 0 ? options.length - 1 : active - 1);
        renderMenu();
        menu?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (menu && active >= 0 && options[active]) add(options[active].tag);
        else add(entry.value);
        entry.value = '';
        active = -1;
        renderMenu();
      } else if (e.key === 'Backspace' && !entry.value && field._tags.length) {
        field._tags.pop();
        paintPills();
        renderMenu();
      } else if (e.key === 'Escape' && menu) {
        e.stopPropagation(); // close the list, not the window
        e.preventDefault();
        closeMenu();
      }
    });

    (tags || []).forEach(t => { const v = norm(t); if (v && !field._tags.some(x => same(x, v))) field._tags.push(v); });
    paintPills();
  }

  /** The tags in a tag field, including any typed but not yet added. */
  static _tagFieldValue(id) {
    const field = document.getElementById(id);
    if (!field || !field._tags) return [];
    field._commitTyped?.();
    return [...field._tags];
  }

  // ============================================================================
  // ADD / EDIT MODAL
  // ============================================================================

  static showAddModal() {
    this._openContactModal(null);
  }

  static showEditModal(contactId) {
    const contact = this.contacts.find(c => c.id === contactId);
    if (!contact) return;
    this._openContactModal(contact);
  }

  static _openContactModal(contact) {
    const isEdit = !!contact;
    const modalId = 'orgContactFormModal';
    document.getElementById(modalId)?.remove();

    const html = `
      <div class="modal fade" id="${modalId}" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${isEdit ? 'Edit Contact' : 'Add Contact'}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <form id="contactForm" novalidate>
                <div class="row g-3 mb-3">
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold">First Name <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" id="cfName" value="${isEdit ? this._esc(contact.name) : ''}" placeholder="First name" maxlength="100" required>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold">Last Name</label>
                    <input type="text" class="form-control" id="cfLastName" value="${isEdit ? this._esc(contact.lastName || '') : ''}" placeholder="Last name" maxlength="100">
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label fw-semibold">Email</label>
                  <input type="email" class="form-control" id="cfEmail" value="${isEdit ? this._esc(contact.email || '') : ''}" placeholder="email@example.com">
                </div>
                <div class="mb-3">
                  <label class="form-label fw-semibold">Phone</label>
                  <input type="tel" class="form-control" id="cfPhone" value="${isEdit ? this._esc(contact.phone || '') : ''}" placeholder="+1 555 000 0000">
                </div>
                <div class="row g-3 mb-3">
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold">Company</label>
                    <input type="text" class="form-control" id="cfCompany" value="${isEdit ? this._esc(contact.company || '') : ''}" placeholder="Acme Corp" maxlength="150">
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label fw-semibold">Job Title</label>
                    <input type="text" class="form-control" id="cfJobTitle" value="${isEdit ? this._esc(contact.jobTitle || '') : ''}" placeholder="Marketing Manager" maxlength="150">
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label fw-semibold">Address</label>
                  <input type="text" class="form-control" id="cfAddress" value="${isEdit ? this._esc(contact.address || '') : ''}" placeholder="123 Main St, New York, NY">
                </div>
                <div class="mb-3">
                  <label class="form-label fw-semibold" for="cfTagsEntry">Tags</label>
                  ${this._tagFieldHtml('cfTags', 'Choose or type a tag')}
                  <div class="form-text">Pick a tag from the list, or type a new one and press Enter.</div>
                </div>
                <div class="mb-3">
                  <label class="form-label fw-semibold">Notes</label>
                  <textarea class="form-control" id="cfNotes" rows="2" placeholder="Optional notes...">${isEdit ? this._esc(contact.notes || '') : ''}</textarea>
                </div>
                <div id="contactFormError" class="alert alert-danger d-none"></div>
              </form>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-org-primary" id="saveContactBtn" onclick="OrgContacts.saveContact('${isEdit ? contact.id : ''}')">
                ${isEdit ? 'Save Changes' : 'Add Contact'}
              </button>
            </div>
          </div>
        </div>
      </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
    const modalEl = document.getElementById(modalId);
    this._wireTagField('cfTags', isEdit ? contact.tags : []);
    const modal = new bootstrap.Modal(modalEl);
    modal.show();
    modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove());
  }

  static async saveContact(contactId) {
    const name = document.getElementById('cfName')?.value.trim();
    const lastName = document.getElementById('cfLastName')?.value.trim() || '';
    const email = document.getElementById('cfEmail')?.value.trim();
    const phone = document.getElementById('cfPhone')?.value.trim();
    const company = document.getElementById('cfCompany')?.value.trim() || '';
    const jobTitle = document.getElementById('cfJobTitle')?.value.trim() || '';
    const address = document.getElementById('cfAddress')?.value.trim() || '';
    const tags = this._tagFieldValue('cfTags');
    const notes = document.getElementById('cfNotes')?.value.trim();
    const errorEl = document.getElementById('contactFormError');

    // Validate
    if (!name) {
      this._showFormError(errorEl, 'First name is required.');
      return;
    }
    if (!email && !phone) {
      this._showFormError(errorEl, 'At least one of email or phone is required.');
      return;
    }

    const btn = document.getElementById('saveContactBtn');
    if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
    if (errorEl) errorEl.classList.add('d-none');

    try {
      const body = { name, lastName, email, phone, company, jobTitle, address, tags, notes };
      const isEdit = !!contactId;

      if (isEdit) {
        await window.OrgContext.orgFetch(`/contacts/${contactId}`, {
          method: 'PUT',
          body: JSON.stringify(body)
        });
      } else {
        await window.OrgContext.orgFetch('/contacts', {
          method: 'POST',
          body: JSON.stringify(body)
        });
      }

      bootstrap.Modal.getInstance(document.getElementById('orgContactFormModal'))?.hide();
      await this.loadContacts();
    } catch (err) {
      const msg = err.message || 'Failed to save contact.';
      this._showFormError(errorEl, msg);
      if (btn) { btn.disabled = false; btn.textContent = contactId ? 'Save Changes' : 'Add Contact'; }
    }
  }

  // ============================================================================
  // CONTACT DETAIL MODAL
  // ============================================================================

  static async showDetailModal(contactId) {
    const contact = this.contacts.find(c => c.id === contactId);
    if (!contact) return;

    const modalId = 'orgContactDetailModal';
    document.getElementById(modalId)?.remove();

    const html = `
      <div class="modal fade" id="${modalId}" tabindex="-1">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title"><i class="bi bi-person-circle me-2"></i>${this._esc(contact.name)}${contact.lastName ? ' ' + this._esc(contact.lastName) : ''}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <div class="row g-3 mb-4">
                <div class="col-sm-6">
                  <div class="contact-detail-field">
                    <div class="detail-label text-muted small mb-1"><i class="bi bi-envelope me-1"></i>Email</div>
                    <div>${contact.email ? `<a href="mailto:${this._esc(contact.email)}">${this._esc(contact.email)}</a>` : '<span class="text-muted">—</span>'}</div>
                  </div>
                </div>
                <div class="col-sm-6">
                  <div class="contact-detail-field">
                    <div class="detail-label text-muted small mb-1"><i class="bi bi-telephone me-1"></i>Phone</div>
                    <div>${contact.phone ? this._esc(contact.phone) : '<span class="text-muted">—</span>'}</div>
                  </div>
                </div>
                ${(contact.company || contact.jobTitle) ? `
                <div class="col-sm-6">
                  <div class="contact-detail-field">
                    <div class="detail-label text-muted small mb-1"><i class="bi bi-building me-1"></i>Company</div>
                    <div>${contact.company ? this._esc(contact.company) : '<span class="text-muted">—</span>'}</div>
                  </div>
                </div>
                <div class="col-sm-6">
                  <div class="contact-detail-field">
                    <div class="detail-label text-muted small mb-1"><i class="bi bi-briefcase me-1"></i>Job Title</div>
                    <div>${contact.jobTitle ? this._esc(contact.jobTitle) : '<span class="text-muted">—</span>'}</div>
                  </div>
                </div>` : ''}
                ${contact.address ? `
                <div class="col-12">
                  <div class="contact-detail-field">
                    <div class="detail-label text-muted small mb-1"><i class="bi bi-geo-alt me-1"></i>Address</div>
                    <div class="small">${this._esc(contact.address)}</div>
                  </div>
                </div>` : ''}
                <div class="col-12">
                  <div class="contact-detail-field">
                    <div class="detail-label text-muted small mb-1"><i class="bi bi-tags me-1"></i>Tags</div>
                    <div class="contact-tags">${(contact.tags || []).length > 0 ? this._tagPills(contact.tags) : '<span class="text-muted">No tags</span>'}</div>
                  </div>
                </div>
                ${contact.notes ? `
                <div class="col-12">
                  <div class="contact-detail-field">
                    <div class="detail-label text-muted small mb-1"><i class="bi bi-journal-text me-1"></i>Notes</div>
                    <div class="small">${this._esc(contact.notes)}</div>
                  </div>
                </div>` : ''}
              </div>

              <h6 class="fw-semibold mb-3"><i class="bi bi-clock-history me-2"></i>Event History</h6>
              <div id="contactHistoryContainer">
                <div class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary"></div>
                  <div class="text-muted small mt-2">Loading history...</div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-org-outline" onclick="OrgContacts.showEditModal('${contact.id}'); bootstrap.Modal.getInstance(document.getElementById('${modalId}'))?.hide();">
                <i class="bi bi-pencil me-1"></i>Edit
              </button>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
    const modalEl = document.getElementById(modalId);
    const modal = new bootstrap.Modal(modalEl);
    modal.show();
    modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove());

    // Load history async
    this._loadAndRenderHistory(contactId);
  }

  static async _loadAndRenderHistory(contactId) {
    const container = document.getElementById('contactHistoryContainer');
    if (!container) return;

    try {
      const data = await window.OrgContext.orgFetch(`/contacts/${contactId}/history`);
      const history = data.history || [];

      if (history.length === 0) {
        container.innerHTML = '<p class="text-muted small">No campaign history found for this contact.</p>';
        return;
      }

      container.innerHTML = `
        <div class="table-responsive">
          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th>Event</th>
                <th>Sent</th>
                <th>Channel</th>
                <th>Delivered</th>
                <th>RSVP</th>
              </tr>
            </thead>
            <tbody>
              ${history.map(h => `
                <tr>
                  <td class="small fw-semibold">${this._esc(h.formTitle)}</td>
                  <td class="small text-muted">${h.sentAt ? this._formatDate(h.sentAt) : '—'}</td>
                  <td class="small">${this._channelBadge(h.channel)}</td>
                  <td class="small">${this._deliveryBadge(h.deliveryStatus)}</td>
                  <td class="small">${this._rsvpBadge(h.rsvpResponse)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>`;
    } catch (err) {
      container.innerHTML = '<p class="text-muted small">Could not load history.</p>';
    }
  }

  // ============================================================================
  // MULTI-SELECT
  // ============================================================================

  static _toggleSelectAll(checked) {
    if (checked) {
      this.filteredContacts.forEach(c => this.selectedContacts.add(c.id));
    } else {
      this.filteredContacts.forEach(c => this.selectedContacts.delete(c.id));
    }
    document.querySelectorAll('.contact-select-cb').forEach(cb => { cb.checked = checked; });
    this._updateSelectionUI();
  }

  static _toggleContactSelection(id, checked) {
    if (checked) {
      this.selectedContacts.add(id);
    } else {
      this.selectedContacts.delete(id);
    }
    this._updateSelectionUI();
  }

  static _updateSelectionUI() {
    const count = this.selectedContacts.size;
    const btn = document.getElementById('deleteSelectedContactsBtn');
    const tagBtn = document.getElementById('tagSelectedContactsBtn');
    const countEl = document.getElementById('selectedContactsCount');
    const countElDelete = document.getElementById('selectedContactsCountDelete');
    const selectAllCb = document.getElementById('selectAllContacts');

    if (btn) btn.classList.toggle('d-none', count === 0);
    if (tagBtn) tagBtn.classList.toggle('d-none', count === 0);
    if (countEl) countEl.textContent = count;
    if (countElDelete) countElDelete.textContent = count;

    if (selectAllCb) {
      const visibleIds = this.filteredContacts.map(c => c.id);
      const selectedVisible = visibleIds.filter(id => this.selectedContacts.has(id));
      selectAllCb.checked = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;
      selectAllCb.indeterminate = selectedVisible.length > 0 && selectedVisible.length < visibleIds.length;
    }
  }

  static async deleteSelected() {
    const ids = [...this.selectedContacts];
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} selected contact${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;

    const btn = document.getElementById('deleteSelectedContactsBtn');
    if (btn) { btn.disabled = true; btn.innerHTML = '<i class="bi bi-hourglass-split me-1"></i>Deleting...'; }

    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        await window.OrgContext.orgFetch(`/contacts/${id}`, { method: 'DELETE' });
        this.selectedContacts.delete(id);
        successCount++;
      } catch {
        failCount++;
      }
    }

    if (btn) { btn.disabled = false; }
    if (failCount > 0) {
      alert(`Deleted ${successCount} contact${successCount !== 1 ? 's' : ''}. Failed to delete ${failCount}.`);
    }
    await this.loadContacts();
  }

  // ============================================================================
  // BULK TAG
  // ============================================================================

  static showBulkTagModal() {
    const count = this.selectedContacts.size;
    if (count === 0) return;

    const modalId = 'orgBulkTagModal';
    document.getElementById(modalId)?.remove();

    // Gather existing tags across all contacts as suggestions
    // A tag is passed by data-tag, not pasted into the onclick: there the
    // escaped quote of a tag like "Women's network" came back as a quote and
    // broke the click.
    const suggestionsHtml = this.allTags.length > 0
      ? `<div class="mb-3">
          <div class="form-text mb-1">Existing tags (click to fill):</div>
          <div class="contact-tags">
            ${this.allTags.map(t => `<button type="button" class="contact-tag is-pick" dir="auto" data-tag="${this._esc(t)}" onclick="document.getElementById('bulkTagInput').value=this.dataset.tag">${this._esc(t)}</button>`).join('')}
          </div>
        </div>`
      : '';

    const html = `
      <div class="modal fade" id="${modalId}" tabindex="-1">
        <div class="modal-dialog modal-sm">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title"><i class="bi bi-tags me-2"></i>Tag ${count} Contact${count !== 1 ? 's' : ''}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              ${suggestionsHtml}
              <div class="mb-3">
                <label class="form-label fw-semibold">Tag</label>
                <input type="text" class="form-control" id="bulkTagInput" placeholder="e.g. VIP" maxlength="50" autofocus>
              </div>
              <div id="bulkTagError" class="alert alert-danger d-none small"></div>
            </div>
            <div class="modal-footer flex-column gap-2">
              <button class="btn btn-org-primary w-100" id="bulkAddTagBtn" onclick="OrgContacts.applyBulkTag('add')">
                <i class="bi bi-plus-circle me-1"></i>Add Tag to Selected
              </button>
              <button class="btn btn-outline-secondary w-100" id="bulkRemoveTagBtn" onclick="OrgContacts.applyBulkTag('remove')">
                <i class="bi bi-dash-circle me-1"></i>Remove Tag from Selected
              </button>
            </div>
          </div>
        </div>
      </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
    const modalEl = document.getElementById(modalId);
    const modal = new bootstrap.Modal(modalEl);
    modal.show();
    modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove());

    // Submit on Enter
    document.getElementById('bulkTagInput')?.addEventListener('keydown', e => {
      if (e.key === 'Enter') OrgContacts.applyBulkTag('add');
    });
  }

  static async applyBulkTag(action) {
    // "VIP, HR" is two tags, as everywhere else (it was saved as one tag
    // called "VIP, HR"). An existing tag picked from the list is kept whole.
    const typed = document.getElementById('bulkTagInput')?.value.trim() || '';
    const tagList = this.allTags.includes(typed) ? [typed] : typed.split(',').map(t => t.trim()).filter(Boolean);
    const errorEl = document.getElementById('bulkTagError');

    if (!tagList.length) {
      if (errorEl) { errorEl.textContent = 'Please enter a tag.'; errorEl.classList.remove('d-none'); }
      return;
    }
    if (errorEl) errorEl.classList.add('d-none');

    const ids = [...this.selectedContacts];
    const addBtn = document.getElementById('bulkAddTagBtn');
    const removeBtn = document.getElementById('bulkRemoveTagBtn');
    if (addBtn) addBtn.disabled = true;
    if (removeBtn) removeBtn.disabled = true;

    let successCount = 0;
    let failCount = 0;

    for (const id of ids) {
      const contact = this.contacts.find(c => c.id === id);
      if (!contact) continue;

      const currentTags = contact.tags || [];
      let newTags;
      if (action === 'add') {
        newTags = [...currentTags, ...tagList.filter(t => !currentTags.includes(t))];
      } else {
        newTags = currentTags.filter(t => !tagList.includes(t));
      }
      if (newTags.length === currentTags.length && newTags.every((t, i) => t === currentTags[i])) continue;

      try {
        await window.OrgContext.orgFetch(`/contacts/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: contact.name, lastName: contact.lastName || '', email: contact.email, phone: contact.phone, company: contact.company || '', jobTitle: contact.jobTitle || '', address: contact.address || '', tags: newTags, notes: contact.notes || '' })
        });
        successCount++;
      } catch {
        failCount++;
      }
    }

    bootstrap.Modal.getInstance(document.getElementById('orgBulkTagModal'))?.hide();

    if (failCount > 0) {
      alert(`Updated ${successCount} contact${successCount !== 1 ? 's' : ''}. Failed to update ${failCount}.`);
    }

    await this.loadContacts();
  }

  // ============================================================================
  // DELETE
  // ============================================================================

  // The name is looked up here, not passed in the onclick: there an escaped
  // apostrophe ("O'Brien") came back as a quote and the button did nothing.
  static confirmDelete(contactId) {
    const c = this.contacts.find(x => x.id === contactId);
    const name = c ? ([c.name, c.lastName].filter(Boolean).join(' ') || c.email || c.phone || 'this contact') : 'this contact';
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    this.deleteContact(contactId);
  }

  static async deleteContact(contactId) {
    try {
      await window.OrgContext.orgFetch(`/contacts/${contactId}`, { method: 'DELETE' });
      await this.loadContacts();
    } catch (err) {
      alert('Failed to delete contact: ' + (err.message || 'Unknown error'));
    }
  }

  // ============================================================================
  // IMPORT (task #1071, Shahar)
  // Choose a CSV or vCard file -> match the CSV's columns to contact fields ->
  // see what will import and which rows will not, and why -> import. A file
  // whose headings were not recognised used to end in one alert, "No valid
  // contacts found in file.", with nothing to act on.
  // ============================================================================

  static IMPORT_FIELDS = [
    { key: 'name', label: 'First Name', required: true },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email Address' },
    { key: 'phone', label: 'Phone Number' },
    { key: 'company', label: 'Company' },
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'address', label: 'Address' },
    { key: 'tags', label: 'Tags / Groups' },
    { key: 'notes', label: 'Notes' }
  ];

  // The order columns are guessed in: Last Name before First Name, so a "Last
  // Name" column is never taken for the first name.
  static IMPORT_GUESS_ORDER = ['email', 'phone', 'lastName', 'name', 'company', 'jobTitle', 'address', 'tags', 'notes'];

  static triggerImport() {
    const modalId = 'orgContactImportModal';
    document.getElementById(modalId)?.remove();
    this._import = { step: 'choose' };
    document.body.insertAdjacentHTML('beforeend', `
      <div class="modal fade org-import-modal" id="${modalId}" tabindex="-1" aria-labelledby="orgContactImportTitle">
        <div class="modal-dialog modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="orgContactImportTitle"><i class="bi bi-upload me-2"></i>Import Contacts</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body" id="orgImportBody"></div>
            <div class="modal-footer" id="orgImportFooter"></div>
          </div>
        </div>
      </div>`);
    const modalEl = document.getElementById(modalId);
    modalEl.addEventListener('hidden.bs.modal', () => { modalEl.remove(); this._import = null; });
    this._renderImportStep();
    new bootstrap.Modal(modalEl).show();
  }

  static chooseImportFile() {
    document.getElementById('contactsFileInput')?.click();
  }

  static async handleFileImport(input) {
    const file = input.files?.[0];
    if (!file) return;
    input.value = '';
    if (!this._import) this.triggerImport();
    const st = this._import;
    st.error = '';
    st.fileName = file.name;

    try {
      if (!/\.(csv|vcf|vcard|txt)$/i.test(file.name)) {
        st.error = `"${file.name}" is not a CSV or vCard file. From Excel or Google Sheets, save it as CSV (File → Save As / Download → CSV) and choose that file.`;
        return this._renderImportStep();
      }
      const text = await window.FileHelpers.readFile(file);

      // File may have been mangled by the app that exported it; the original
      // characters are unrecoverable here (see detectEncodingDamage).
      if (window.FileHelpers.detectEncodingDamage(text)) {
        if (!confirm(window.FileHelpers.ENCODING_DAMAGE_WARNING.replace('{filename}', file.name))) return;
      }

      if (/\.vcf$|\.vcard$/i.test(file.name) || /^\s*BEGIN:VCARD/i.test(text)) {
        // A vCard says what each value is; nothing to match.
        const cards = window.FileHelpers.parseVCard(text, file.name, { details: true, keepIncomplete: true });
        if (!cards.length) {
          st.error = `We couldn't find any contact cards in "${file.name}". A vCard file starts with BEGIN:VCARD - export it again from your phone or contacts app.`;
          return this._renderImportStep();
        }
        st.kind = 'vcard';
        st.rows = cards.map(c => ({
          where: `Card ${c.row}`,
          contact: {
            name: c.name || '', lastName: c.lastName || '', email: c.email || '', phone: c.phone || '',
            company: c.company || '', jobTitle: c.jobTitle || '', address: c.address || '',
            notes: c.notes || '', tags: c.tags || []
          }
        }));
        st.step = 'preview';
      } else {
        const table = window.FileHelpers.contactCsvTable(text, this.IMPORT_GUESS_ORDER);
        if (!table.headers.length || !table.rows.length) {
          st.error = `"${file.name}" has no contacts to read. Its first row should be the column headings, with one contact on each row below it.`;
          return this._renderImportStep();
        }
        st.kind = 'csv';
        st.table = table;
        st.map = { ...table.guess };
        // "Groups" is a guest list's household in the shared headings; a
        // Corporate contact has no household, so here it is Tags / Groups.
        if (st.map.tags < 0) {
          const taken = new Set(Object.values(st.map));
          const col = table.headers.findIndex((h, i) => !taken.has(i) && /^\s*(groups?|categor(y|ies))\s*$/i.test(h));
          if (col !== -1) st.map.tags = col;
        }
        st.step = 'map';
      }
    } catch (err) {
      st.error = `We couldn't read "${file.name}": ${err.message || 'unknown error'}.`;
    }
    this._renderImportStep();
  }

  static downloadSampleCsv() {
    this._downloadCsv([
      ['First Name', 'Last Name', 'Email', 'Phone', 'Company', 'Job Title', 'Address', 'Tags', 'Notes'],
      ['Dana', 'Levi', 'dana.levi@example.com', '+1 555 010 0101', 'Acme Inc.', 'Head of HR', '12 Main St, Springfield', 'VIP; HR', 'Prefers email'],
      ['Omar', 'Haddad', '', '+44 20 7946 0102', 'Northwind', 'Engineer', '', 'Engineering', '']
    ], 'contacts-sample.csv');
  }

  /** One contact's reason not to import, or '' (the server's rule, said up front). */
  static _importIssue(c) {
    if (!c.name) return c.lastName ? 'no first name' : 'no name';
    if (!c.email && !c.phone) return 'no email or phone';
    if (c.email && window.ValidationUtils?.isValidEmail && !window.ValidationUtils.isValidEmail(c.email)) {
      return `"${c.email}" is not a valid email address`;
    }
    return '';
  }

  static _setImportMap(field, value) {
    if (!this._import) return;
    this._import.map[field] = parseInt(value, 10);
    this._import.mapError = '';
  }

  static _importContinue() {
    const st = this._import;
    if (!st) return;
    if (st.map.name < 0) st.mapError = 'Choose the column that holds First Name.';
    else if (st.map.email < 0 && st.map.phone < 0) st.mapError = 'Choose the column that holds Email Address or Phone Number - every contact needs one of them.';
    else st.mapError = '';
    if (st.mapError) return this._renderImportStep();

    st.rows = st.table.rows.map(r => {
      const contact = {};
      this.IMPORT_FIELDS.forEach(f => {
        const col = st.map[f.key];
        contact[f.key] = col >= 0 ? (r.cells[col] || '') : '';
      });
      contact.tags = window.FileHelpers.parseContactTags(contact.tags) || [];
      return { where: `Line ${r.line}`, contact };
    });
    st.step = 'preview';
    this._renderImportStep();
  }

  static _importBack() {
    const st = this._import;
    if (!st) return;
    st.step = st.step === 'preview' && st.kind === 'csv' ? 'map' : 'choose';
    st.error = '';
    this._renderImportStep();
  }

  static _renderImportStep() {
    const st = this._import;
    const body = document.getElementById('orgImportBody');
    const footer = document.getElementById('orgImportFooter');
    if (!st || !body || !footer) return;
    const esc = (s) => this._esc(s);
    const errorHtml = (msg) => msg ? `<div class="alert alert-danger small mb-3" role="alert">${esc(msg)}</div>` : '';

    // A narrow window for the first step, the wide one for matching/preview.
    body.closest('.modal-dialog')?.classList.toggle('modal-lg', st.step !== 'choose');

    if (st.step === 'choose') {
      // One line (task #1096, Shahar - who asked for the format cards in
      // #1071 and found them too much once she saw them). What every contact
      // needs stays in the line: the matching step and the preview enforce
      // it, but saying it before the file is chosen saves a round trip.
      body.innerHTML = `
        ${errorHtml(st.error)}
        <p class="mb-2">Choose a <strong>CSV</strong> or <strong>vCard</strong> (.vcf) file. Each contact needs a first name and an email or phone.</p>
        <button type="button" class="btn btn-link btn-sm p-0" onclick="OrgContacts.downloadSampleCsv()"><i class="bi bi-download me-1"></i>Download sample CSV</button>`;
      footer.innerHTML = `
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn btn-org-primary" onclick="OrgContacts.chooseImportFile()"><i class="bi bi-folder2-open me-1"></i>Choose file</button>`;
      return;
    }

    if (st.step === 'map') {
      const { headers, rows } = st.table;
      const sample = (col) => {
        const v = (rows.find(r => r.cells[col]) || { cells: [] }).cells[col] || '';
        return v.length > 28 ? v.slice(0, 27) + '…' : v;
      };
      const options = (selected) => `<option value="-1"${selected < 0 ? ' selected' : ''}>— Don't import —</option>`
        + headers.map((h, i) => `<option value="${i}"${i === selected ? ' selected' : ''}>${esc(h || `Column ${i + 1}`)}${sample(i) ? ` (e.g. ${esc(sample(i))})` : ''}</option>`).join('');
      body.innerHTML = `
        ${errorHtml(st.mapError)}
        <p class="mb-3">Match the columns in <strong>${esc(st.fileName)}</strong> (${rows.length} row${rows.length === 1 ? '' : 's'}) to contact fields. We matched the headings we recognised - change any that are wrong.</p>
        <div class="org-import-map">
          ${this.IMPORT_FIELDS.map(f => `
            <label class="org-import-map-label" for="orgImportMap_${f.key}">${esc(f.label)}${f.required ? ' <span class="text-danger" aria-hidden="true">*</span>' : ''}</label>
            <select class="form-select form-select-sm" id="orgImportMap_${f.key}" onchange="OrgContacts._setImportMap('${f.key}', this.value)">${options(st.map[f.key])}</select>`).join('')}
        </div>
        <p class="small text-muted mt-3 mb-0"><span class="text-danger">*</span> Required. Each contact also needs an Email Address or a Phone Number. Tags can be several, separated by commas or semicolons.</p>`;
      footer.innerHTML = `
        <button type="button" class="btn btn-org-ghost me-auto" onclick="OrgContacts._importBack()">Back</button>
        <button type="button" class="btn btn-org-primary" onclick="OrgContacts._importContinue()">Continue</button>`;
      return;
    }

    // Preview
    const checked = st.rows.map(r => ({ ...r, issue: this._importIssue(r.contact) }));
    const ready = checked.filter(r => !r.issue);
    const bad = checked.filter(r => r.issue);
    st.ready = ready.map(r => r.contact);
    const shown = ready.slice(0, 5);
    const fullName = (c) => [c.name, c.lastName].filter(Boolean).join(' ');

    body.innerHTML = `
      <div class="org-import-summary">
        <div><strong>${checked.length}</strong> ${st.kind === 'vcard' ? 'cards' : 'rows'} read</div>
        <div class="is-ok"><strong>${ready.length}</strong> ready to import</div>
        ${bad.length ? `<div class="is-bad"><strong>${bad.length}</strong> can't be imported</div>` : ''}
      </div>
      ${ready.length ? `
        <div class="table-responsive mb-3">
          <table class="table table-sm mb-0">
            <thead><tr><th>Name</th><th>Email</th><th>Phone</th></tr></thead>
            <tbody>
              ${shown.map(r => `<tr>
                <td class="small">${esc(fullName(r.contact))}</td>
                <td class="small text-muted">${esc(r.contact.email || '—')}</td>
                <td class="small text-muted">${esc(r.contact.phone || '—')}</td>
              </tr>`).join('')}
              ${ready.length > shown.length ? `<tr><td colspan="3" class="text-muted small text-center">…and ${ready.length - shown.length} more</td></tr>` : ''}
            </tbody>
          </table>
        </div>` : ''}
      ${bad.length ? `
        <div class="org-import-issues mb-3">
          <div class="fw-semibold small mb-1">Not imported - fix ${bad.length === 1 ? 'this row' : 'these rows'} in your file and import it again:</div>
          <ul>
            ${bad.slice(0, 50).map(r => `<li><strong>${esc(r.where)}</strong>${fullName(r.contact) ? ` (${esc(fullName(r.contact))})` : ''}: ${esc(r.issue)}</li>`).join('')}
            ${bad.length > 50 ? `<li class="text-muted">…and ${bad.length - 50} more</li>` : ''}
          </ul>
        </div>` : ''}
      ${ready.length ? `
        <div class="mb-3">
          <label class="form-label fw-semibold small" for="importTagsEntry">Apply Tags to All Imported Contacts <span class="text-muted fw-normal">(optional)</span></label>
          ${this._tagFieldHtml('importTags', 'Choose or type a tag')}
          <div class="form-text">Added to every contact in this import. Contacts already in your list (same email or phone) are skipped.</div>
        </div>` : ''}
      <div id="importResult" class="d-none"></div>`;
    this._wireTagField('importTags', []);
    footer.innerHTML = `
      <button type="button" class="btn btn-org-ghost me-auto" onclick="OrgContacts._importBack()">Back</button>
      <button type="button" class="btn btn-org-primary" id="confirmImportBtn" onclick="OrgContacts._doImport()"${ready.length ? '' : ' disabled'}>
        <i class="bi bi-upload me-1"></i>Import ${ready.length} Contact${ready.length === 1 ? '' : 's'}
      </button>`;
  }

  static async _doImport() {
    const contacts = this._import?.ready || [];
    const btn = document.getElementById('confirmImportBtn');
    const resultEl = document.getElementById('importResult');
    if (!contacts.length) return;
    if (btn) { btn.disabled = true; btn.textContent = 'Importing...'; }

    const importTags = this._tagFieldValue('importTags');

    const contactsToImport = importTags.length > 0
      ? contacts.map(c => ({ ...c, tags: [...new Set([...(c.tags || []), ...importTags])] }))
      : contacts;

    try {
      const data = await window.OrgContext.orgFetch('/contacts/import', {
        method: 'POST',
        body: JSON.stringify({ contacts: contactsToImport })
      });

      if (resultEl) {
        const dup = data.duplicates ?? data.skipped ?? 0;
        resultEl.className = 'alert alert-success';
        resultEl.textContent = `✓ Imported ${data.imported} contact${data.imported === 1 ? '' : 's'}.`
          + (dup > 0 ? ` ${dup} already in your list (same email or phone) - skipped.` : '')
          + (data.incomplete > 0 ? ` ${data.incomplete} had no name or no email and phone - skipped.` : '');
      }

      // Done (task #1097): nothing to go Back to or re-tag, so the footer is
      // one centred Close. The list behind is refreshed now, so it already
      // shows the new contacts when the window closes.
      const footer = document.getElementById('orgImportFooter');
      if (footer) {
        footer.classList.add('justify-content-center');
        footer.innerHTML = '<button type="button" class="btn btn-org-primary px-4" data-bs-dismiss="modal">Close</button>';
        footer.querySelector('button').focus();
      }
      document.getElementById('importTags')?.closest('.mb-3')?.classList.add('d-none');
      await this.loadContacts();
    } catch (err) {
      if (resultEl) {
        resultEl.className = 'alert alert-danger';
        resultEl.textContent = 'Import failed: ' + (err.message || 'Unknown error');
      }
      if (btn) { btn.disabled = false; btn.textContent = 'Retry'; }
    }
  }

  // ============================================================================
  // CSV EXPORT
  // ============================================================================

  static exportCSV() {
    if (this.contacts.length === 0) {
      alert('No contacts to export.');
      return;
    }

    // The import's own columns (task #1071), so an export imports back whole:
    // it used to leave out last name, company, job title and address.
    const rows = [['First Name', 'Last Name', 'Email', 'Phone', 'Company', 'Job Title', 'Address', 'Tags', 'Notes']];
    this.contacts.forEach(c => {
      rows.push([
        c.name || '',
        c.lastName || '',
        c.email || '',
        c.phone || '',
        c.company || '',
        c.jobTitle || '',
        c.address || '',
        (c.tags || []).join('; '),
        c.notes || ''
      ]);
    });
    this._downloadCsv(rows, 'contacts.csv');
  }

  static _downloadCsv(rows, fileName) {
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    // Lead with a UTF-8 BOM or Excel renders Hebrew/Arabic names as mojibake —
    // this export sits beside Import, so it is the obvious round trip.
    const blob = new Blob([String.fromCharCode(0xFEFF) + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  static _showLoading(show) {
    const loading = document.getElementById('contactsLoading');
    const content = document.getElementById('contactsContent');
    if (loading) loading.style.display = show ? 'block' : 'none';
    if (content) content.style.display = show ? 'none' : 'block';
  }

  static _showError(msg) {
    const content = document.getElementById('contactsContent');
    if (content) {
      content.style.display = 'block';
      content.innerHTML = `<div class="alert alert-danger">${this._esc(msg)}</div>`;
    }
  }

  static _showFormError(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.classList.remove('d-none');
  }

  static _esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  static _formatDate(ts) {
    if (!ts) return '—';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts._seconds ? ts._seconds * 1000 : ts);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return '—'; }
  }

  static _channelBadge(channel) {
    const map = { email: 'secondary', mobile: 'success', both: 'primary', whatsapp: 'success' };
    const color = map[channel] || 'secondary';
    return `<span class="badge bg-${color}">${this._esc(channel || '—')}</span>`;
  }

  static _deliveryBadge(status) {
    const map = { delivered: 'success', sent: 'info', failed: 'danger', read: 'success', opened: 'success' };
    const color = map[(status || '').toLowerCase()] || 'secondary';
    return `<span class="badge bg-${color}">${this._esc(status || '—')}</span>`;
  }

  static _rsvpBadge(rsvp) {
    if (!rsvp) return '<span class="text-muted">—</span>';
    const lower = rsvp.toLowerCase();
    const color = lower === 'yes' || lower === 'attending' ? 'success'
      : lower === 'no' || lower === 'not attending' ? 'danger'
      : 'warning';
    return `<span class="badge bg-${color}">${this._esc(rsvp)}</span>`;
  }
}

window.OrgContacts = OrgContacts;
